import React from 'react';
import { useTheme } from '../../../theme/ThemeContext';
import {
  STAFF_ROLE_OPTIONS,
  STAFF_ROLES,
  isKnownSpecialisation,
} from '../../../constants/staff';
import { SPECIALIZATION_CATALOGUE, SPECIALIZATION_GROUPS } from '../../../constants/specializations';
import SpecialityCombobox from '../SpecialityCombobox';
import SignatureEditor from '../SignatureEditor/SignatureEditor';
import './StaffFormRow.css';

/**
 * StaffFormRow — Single reusable staff member form row
 *
 * Used in both Staff Onboarding and Manage Staff pages.
 *
 * Props:
 *   data         — { first_name, last_name, mobile, email, role, signature_html? }
 *   errors       — { first_name?, last_name?, mobile?, email?, role? }
 *   index        — row number (0-based)
 *   onChange      — (index, fieldName, value) => void
 *   onRemove      — (index) => void | null (null hides remove button)
 *   disabled      — boolean
 *   showRemove    — boolean (default true)
 *   departments   — [{ dept_id, name, active }] optional dept list (source of
 *                   truth: departmentStore). Drives the optional Department
 *                   picker; hidden for ADMIN and when there are 0 active depts.
 *   memberId      — the staff member's real member_id. Only ever passed by
 *                   the EDIT-row call site (ManageStaffModal) — "Add New
 *                   Staff" rows pass none, since the signature GET/PUT
 *                   endpoints are member_id-scoped and the member doesn't
 *                   exist yet at that point. Gates the Signature section
 *                   below (DOCTOR-only AND edit-only).
 *   signatureLoading — true while the parent's GET .../signature hydration
 *                   fetch is in flight for this row.
 */
// Combobox options carry the catalogue group (module-level: stable reference).
const SPECIALIZATION_OPTIONS_WITH_GROUP = SPECIALIZATION_CATALOGUE.map(
  ({ value, label, group }) => ({ value, label, group })
);

const StaffFormRow = ({
  data,
  errors = {},
  index,
  onChange,
  onRemove,
  disabled = false,
  showRemove = true,
  departments = [],
  memberId = null,
  signatureLoading = false,
}) => {
  const { theme } = useTheme();

  // Only active departments are assignable. A dept object omitting `active`
  // (active === undefined) is treated as active (matches store convention).
  const activeDepts = (departments || []).filter((d) => d.active !== false);
  // Optional Department picker: shown for every role EXCEPT Admin, and only
  // when the org has at least one active department (else hide entirely — no
  // empty/only-"No department" dropdown).
  const showDepartment =
    data.role !== STAFF_ROLES.ADMIN && activeDepts.length > 0;

  const handleChange = (field) => (e) => {
    onChange(index, field, e.target.value);
  };

  const inputStyle = {
    backgroundColor: theme.background.surface,
    color: theme.text.primary,
    borderColor: theme.border.input,
  };

  return (
    <div
      className="staff-row-card"
      style={{
        backgroundColor: theme.background.surface,
        borderColor: theme.border.default,
      }}
    >
      <div className="staff-row-header">
        <span className="staff-row-number" style={{ color: theme.text.secondary }}>
          Staff #{index + 1}
        </span>
        {showRemove && onRemove && (
          <button
            type="button"
            className="staff-row-remove"
            onClick={() => onRemove(index)}
            disabled={disabled}
            title="Remove staff member"
            style={{ color: theme.brand.error }}
          >
            ✕
          </button>
        )}
      </div>

      <div className="staff-row-fields">
        {/* First Name + Last Name */}
        <div className="staff-field-row">
          <div className="staff-field">
            <label style={{ color: theme.text.primary }}>
              First Name <span className="staff-required">*</span>
            </label>
            <input
              type="text"
              value={data.first_name}
              onChange={handleChange('first_name')}
              placeholder="First name"
              disabled={disabled}
              style={inputStyle}
              maxLength={50}
            />
            {errors.first_name && (
              <span className="staff-field-error">{errors.first_name}</span>
            )}
          </div>
          <div className="staff-field">
            <label style={{ color: theme.text.primary }}>
              Last Name <span className="staff-required">*</span>
            </label>
            <input
              type="text"
              value={data.last_name}
              onChange={handleChange('last_name')}
              placeholder="Last name"
              disabled={disabled}
              style={inputStyle}
              maxLength={50}
            />
            {errors.last_name && (
              <span className="staff-field-error">{errors.last_name}</span>
            )}
          </div>
        </div>

        {/* Mobile + Email */}
        <div className="staff-field-row">
          <div className="staff-field">
            <label style={{ color: theme.text.primary }}>
              Mobile <span className="staff-required">*</span>
            </label>
            <input
              type="tel"
              value={data.mobile}
              onChange={handleChange('mobile')}
              placeholder="+91 98765 43210"
              disabled={disabled}
              style={inputStyle}
              maxLength={15}
            />
            {errors.mobile && (
              <span className="staff-field-error">{errors.mobile}</span>
            )}
          </div>
          <div className="staff-field">
            <label style={{ color: theme.text.primary }}>Email</label>
            <input
              type="email"
              value={data.email}
              onChange={handleChange('email')}
              placeholder="email@example.com"
              disabled={disabled}
              style={inputStyle}
            />
            {errors.email && (
              <span className="staff-field-error">{errors.email}</span>
            )}
          </div>
        </div>

        {/* Role + Specialization */}
        <div className="staff-field-row">
          <div className="staff-field staff-field-role">
            <label style={{ color: theme.text.primary }}>
              Role <span className="staff-required">*</span>
            </label>
            <select
              value={data.role}
              onChange={(e) => {
                const newRole = e.target.value;
                onChange(index, 'role', newRole);
                // Clear specialization when switching away from DOCTOR
                if (newRole !== STAFF_ROLES.DOCTOR) {
                  onChange(index, 'specialization', '');
                  onChange(index, 'specialization_other', false);
                  onChange(index, 'registration_no', '');
                  // Local-only — does NOT delete anything server-side, so
                  // re-promoting this person back to DOCTOR doesn't force
                  // them to redo their signature.
                  onChange(index, 'signature_html', '');
                  onChange(index, 'signature_font_family', null);
                  onChange(index, 'signature_font_size', null);
                }
                // Clear role_name when switching away from OTHER_STAFF
                if (newRole !== STAFF_ROLES.OTHER_STAFF) {
                  onChange(index, 'role_name', '');
                }
                // Clear lab-tech registration fields when leaving LAB_TECHNICIAN
                if (newRole !== STAFF_ROLES.LAB_TECHNICIAN) {
                  onChange(index, 'registration_number', '');
                  onChange(index, 'registration_authority', '');
                }
                // Admin never has a department — clear any prior selection.
                if (newRole === STAFF_ROLES.ADMIN) {
                  onChange(index, 'department_id', '');
                }
              }}
              disabled={disabled}
              style={inputStyle}
            >
              {STAFF_ROLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {errors.role && (
              <span className="staff-field-error">{errors.role}</span>
            )}
          </div>

          {data.role === STAFF_ROLES.DOCTOR && (
            <div className="staff-field staff-field-specialization">
              <label style={{ color: theme.text.primary }}>
                Specialization <span className="staff-required">*</span>
              </label>
              <SpecialityCombobox
                id={`speciality-${index}`}
                value={data.specialization || ''}
                onChange={(v) => {
                  // The combobox emits '' when "Other" is picked (then the
                  // typed text) — remember Other so validation can say so.
                  onChange(index, 'specialization', v);
                  onChange(index, 'specialization_other', !isKnownSpecialisation(v));
                }}
                options={SPECIALIZATION_OPTIONS_WITH_GROUP}
                groups={SPECIALIZATION_GROUPS}
                disabled={disabled}
              />
              {errors.specialization && (
                <span className="staff-field-error">{errors.specialization}</span>
              )}
            </div>
          )}

          {data.role === STAFF_ROLES.OTHER_STAFF && (
            <div className="staff-field">
              <label style={{ color: theme.text.primary }}>
                Role Name <span className="staff-required">*</span>
              </label>
              <input
                type="text"
                value={data.role_name || ''}
                onChange={handleChange('role_name')}
                placeholder="Security Guard"
                disabled={disabled}
                style={inputStyle}
                maxLength={50}
              />
              {errors.role_name && (
                <span className="staff-field-error">{errors.role_name}</span>
              )}
            </div>
          )}
        </div>

        {/* Registration No (Doctors only, optional) */}
        {data.role === STAFF_ROLES.DOCTOR && (
          <div className="staff-field-row">
            <div className="staff-field">
              <label style={{ color: theme.text.primary }}>Registration No.</label>
              <input
                type="text"
                value={data.registration_no || ''}
                onChange={handleChange('registration_no')}
                placeholder="e.g. MCI-12345"
                disabled={disabled}
                style={inputStyle}
                maxLength={50}
              />
            </div>
          </div>
        )}

        {/* Prescription Signature — DOCTOR-only, edit-only (matches the
            existing profile-picture upload precedent of needing a real
            member_id first; never shown on "Add New Staff" rows). Text
            only — an uploaded signature image was deliberately removed
            (not confirmed legally equivalent to a real digital signature
            under India's IT Act, 2000). */}
        {data.role === STAFF_ROLES.DOCTOR && memberId && (
          <div className="staff-field-row">
            <div className="staff-field" style={{ width: '100%' }}>
              <label style={{ color: theme.text.primary }}>Prescription Signature</label>
              <SignatureEditor
                loading={signatureLoading}
                initialSignatureHtml={data.signature_html}
                onTextChange={(html) => onChange(index, 'signature_html', html)}
                fontFamily={data.signature_font_family}
                onFontFamilyChange={(value) => onChange(index, 'signature_font_family', value)}
                fontSize={data.signature_font_size}
                onFontSizeChange={(value) => onChange(index, 'signature_font_size', value)}
                disabled={disabled}
              />
            </div>
          </div>
        )}

        {/* Lab technician registration (both optional) */}
        {data.role === STAFF_ROLES.LAB_TECHNICIAN && (
          <div className="staff-field-row">
            <div className="staff-field">
              <label style={{ color: theme.text.primary }}>Registration Number</label>
              <input
                type="text"
                value={data.registration_number || ''}
                onChange={handleChange('registration_number')}
                placeholder="Optional"
                disabled={disabled}
                style={inputStyle}
                maxLength={50}
              />
            </div>
            <div className="staff-field">
              <label style={{ color: theme.text.primary }}>Registration Authority</label>
              <input
                type="text"
                value={data.registration_authority || ''}
                onChange={handleChange('registration_authority')}
                placeholder="e.g. Karnataka Paramedical Board"
                disabled={disabled}
                style={inputStyle}
                maxLength={100}
              />
            </div>
          </div>
        )}

        {/* Department (optional; hidden for Admin and when org has 0 depts) */}
        {showDepartment && (
          <div className="staff-field-row">
            <div className="staff-field">
              <label style={{ color: theme.text.primary }}>Department</label>
              <select
                value={data.department_id || ''}
                onChange={handleChange('department_id')}
                disabled={disabled}
                style={inputStyle}
              >
                <option value="">No department</option>
                {activeDepts.map((d) => (
                  <option key={d.dept_id} value={d.dept_id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffFormRow;
